/**
 * High Cost — service layer (`/api/HighCost`).
 *
 * Controlador `HighCostController`. JWT obligatorio en todas las llamadas
 * (el backend lee employeeId/IP del token para auditoría HIPAA). Propiedades
 * de respuesta en camelCase.
 *
 * ⚠️ Convención de `result` del módulo: `result = 1` es ÉXITO, `0` es error
 * (inversa a CareGapsV2). Solo aplica al endpoint POST de pre-sugerencia.
 *
 * Referencia: docs/modulos/Highcost/API_ENDPOINTS_HIGH_COST.md
 */
import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/HighCost`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Quita null/undefined/"" para no enviar query params vacíos.
const buildParams = (filters = {}) => {
  const params = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") params[k] = v;
  });
  return params;
};

// 204 No Content → fallback. Si no, devuelve data.
const unwrap = (response, fallback) => {
  if (response.status === 204) return fallback;
  return response.data ?? fallback;
};

/**
 * Lista paginada de pacientes high cost (Tab Pacientes). Server-side.
 *
 * @param {Object} [filters]
 * @param {number|null} [filters.healthStatusId]
 * @param {number|null} [filters.minGlobalIndicator]  - 3..7
 * @param {number|null} [filters.minTotalCost]
 * @param {string|null} [filters.condition]           - LIKE (ej. "Diabetes")
 * @param {number|null} [filters.locationId]
 * @param {number|null} [filters.pcpEmployeeId]        - filtro exacto (tiene prioridad sobre pcpName)
 * @param {string|null} [filters.pcpName]              - LIKE por prefijo
 * @param {string|null} [filters.gender]               - "M" | "F"
 * @param {boolean} [filters.onlyActive=true]
 * @param {string|null} [filters.search]               - nombre o CIP (LIKE)
 * @param {number} [filters.page=1]
 * @param {number} [filters.pageSize=50]               - 1..500
 *
 * Ejemplo: GET /api/HighCost?minTotalCost=50000&onlyActive=true&page=1&pageSize=50
 *
 * Respuesta (200): { data: [ { highCostId, cipId, memberFullName, age, gender,
 *   metalLevel, healthStatus, globalIndicator, totalCost, inpatientCost,
 *   pharmacyCost, erCost, conditions, conditionCount, presenceStatus,
 *   lastSeenInFile, weeksAbsent, pcpName, pcpTitle, locationId, medicalCenter,
 *   officeLocation } ], page, pageSize }
 *
 * 204 → sin resultados ({ data: [] }). 401 → JWT inválido.
 */
export const getHighCostList = async (filters = {}) => {
  const response = await axios.get(API_URL, {
    headers: authHeaders(),
    params: buildParams(filters),
  });
  return unwrap(response, { data: [], page: filters.page || 1, pageSize: filters.pageSize || 50 });
};

/**
 * Conteo total para paginación. Mismos filtros que getHighCostList salvo page/pageSize.
 *
 * Ejemplo: GET /api/HighCost/count?minTotalCost=50000&onlyActive=true
 * Respuesta (200): { total: 1287 }
 */
export const getHighCostCount = async (filters = {}) => {
  const { page, pageSize, ...rest } = filters;
  const response = await axios.get(`${API_URL}/count`, {
    headers: authHeaders(),
    params: buildParams(rest),
  });
  return response.data?.total ?? 0;
};

/**
 * KPIs del bloque protagonista del análisis.
 *
 * @param {Object} [filters] - { locationId, pcpEmployeeId, pcpName, onlyActive }
 * Ejemplo: GET /api/HighCost/dashboard-kpis?locationId=3&onlyActive=true
 * Respuesta (200): { patients, totalCost, avgCost, top10Pct, newThisWeek, presentReturning }
 */
export const getDashboardKpis = async (filters = {}) => {
  const response = await axios.get(`${API_URL}/dashboard-kpis`, {
    headers: authHeaders(),
    params: buildParams(filters),
  });
  return unwrap(response, null);
};

/**
 * Agregado por Health Status (cohorte clínica).
 *
 * Ejemplo: GET /api/HighCost/by-health-status?onlyActive=true
 * Respuesta (200): [ { healthStatus, displayOrder, patients, totalCost } ]  (ordenado por costo desc)
 * 204 → [].
 */
export const getByHealthStatus = async (filters = {}) => {
  const response = await axios.get(`${API_URL}/by-health-status`, {
    headers: authHeaders(),
    params: buildParams(filters),
  });
  return unwrap(response, []);
};

/**
 * Composición del gasto por categoría de servicio.
 *
 * Ejemplo: GET /api/HighCost/cost-composition?locationId=3
 * Respuesta (200): [ { category, cost, pct } ]  (ordenado desc)
 * 204 → [].
 */
export const getCostComposition = async (filters = {}) => {
  const response = await axios.get(`${API_URL}/cost-composition`, {
    headers: authHeaders(),
    params: buildParams(filters),
  });
  return unwrap(response, []);
};

/**
 * Distribución social global + backlog de revisión.
 *
 * Ejemplo: GET /api/HighCost/social-distribution?onlyActive=true
 * Respuesta (200): [ { globalCategory, globalCategoryKey, displayOrder, total, autoSuggested, confirmed } ]
 * 204 → [].
 */
export const getSocialDistribution = async (filters = {}) => {
  const response = await axios.get(`${API_URL}/social-distribution`, {
    headers: authHeaders(),
    params: buildParams(filters),
  });
  return unwrap(response, []);
};

/**
 * Agregado por PCP (comparación de paneles).
 *
 * @param {Object} [filters] - { locationId, pcpEmployeeId, pcpName, onlyActive, top }
 * Ejemplo: GET /api/HighCost/by-pcp?top=3&onlyActive=true
 * Respuesta (200): [ { topApplied, pcpName, pcpTitle, patients, totalCost } ]  (ordenado por costo desc)
 * 204 → [].
 */
export const getByPcp = async (filters = {}) => {
  const response = await axios.get(`${API_URL}/by-pcp`, {
    headers: authHeaders(),
    params: buildParams(filters),
  });
  return unwrap(response, []);
};

/**
 * Trayectoria histórica del paciente (snapshots, del más antiguo al más reciente).
 *
 * @param {string} cipId
 * Ejemplo: GET /api/HighCost/ACA0009123/trajectory
 * Respuesta (200): [ { sourceUploadDate, totalCost, inpatientCost, outpatientCost,
 *   erCost, pharmacyCost, globalIndicator, conditionCount, healthStatus, eventType } ]
 * eventType ∈ { first_seen, snapshot, reappeared }. 204 → [].
 */
export const getPatientTrajectory = async (cipId) => {
  const response = await axios.get(`${API_URL}/${encodeURIComponent(cipId)}/trajectory`, {
    headers: authHeaders(),
  });
  return unwrap(response, []);
};

/**
 * Detalle del paciente: { header, conditions }.
 *
 * @param {string} cipId
 * Ejemplo: GET /api/HighCost/ACA0009123
 * Respuesta (200): { header: { ...todos los campos del paciente + desglose de costo },
 *                    conditions: [ { conditionName, behavioral } ] (conductuales primero) }
 * 404 → { message } cuando no hay registro High Cost para el CIP.
 */
export const getPatientDetail = async (cipId) => {
  const response = await axios.get(`${API_URL}/${encodeURIComponent(cipId)}`, {
    headers: authHeaders(),
  });
  return response.data ?? null;
};

/**
 * Dispara la pre-sugerencia SDOH para UN paciente (SP_SUGGEST_SOCIAL_STRATIFICATION).
 *
 * @param {string} cipId
 * Ejemplo: POST /api/HighCost/ACA0009123/suggest-social-stratification
 * Respuesta (200): { result: 1, message }   ← result=1 ÉXITO
 * Error (500):     { result: 0, message }   ← result=0 ERROR (capturado en el SP)
 * 400 → cipId vacío.
 */
export const suggestSocialStratification = async (cipId) => {
  const response = await axios.post(
    `${API_URL}/${encodeURIComponent(cipId)}/suggest-social-stratification`,
    null,
    { headers: authHeaders() }
  );
  return response.data ?? { result: 0, message: "Empty response" };
};
